<!-- eslint-disable vue/max-lines-per-block -->
<script setup lang="ts">
import { CheckBadgeIcon, PlusCircleIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { storeToRefs } from 'pinia'
import { useField, useFieldArray, useFormContext } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

import type { AddressDynamic } from '@/types/external/2work/doctypes/generatorOutput/Contacts/AddressDynamic'
import type { TWOrganizationAIResponse } from '@/types/external/2work/doctypes/generatorOutput/ERP2Work/TWOrganizationAIResponse'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import CollapsableContent from '@/components/common/layout/CollapsableContent.vue'
import DivideContent from '@/components/common/layout/DivideContent.vue'
import ButtonTertiary from '@/components/common/ui/buttons/ButtonTertiary.vue'
import FormSelect from '@/components/common/ui/inputs/FormSelect.vue'
import InputGrid from '@/components/common/ui/inputs/InputGrid.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import ThreedotActionsOnHover from '@/components/common/ui/inputs/ThreedotActionsOnHover.vue'
import ContextMenuItem from '@/components/common/ui/overlays/ContextMenuItem.vue'
import GERMAN_STATES_OPTIONS from '@/constants/germanStateOptions.ts'
import { useBackendConstantsStore } from '@/stores/backendConstantsStore'
import {
  findOptionValueFromAiText,
  hasMatchingAiAddress,
  resolveAiAddressTitle,
} from '@/utilities/organizationAi'
import { translationFunctionDefault } from '@/utilities/translations'

type SetPrimaryFunction = (address: {
  tw_primary_address: AddressDynamic
}) => Promise<{ tw_addresses?: AddressDynamic[] | null }>

interface SectionAddressesProps {
  addAddressesDisabled?: boolean | undefined
  aiData?: null | TWOrganizationAIResponse | undefined
  checkPrimary?: ((address: AddressDynamic) => boolean) | undefined
  hasAlternativeRecipient?: boolean | undefined
  inCreateContext?: boolean
  isPersonCustomer?: boolean | undefined
  onSubmit?: undefined | VoidFunction
  setPrimary?: SetPrimaryFunction | undefined
}

const props = defineProps<SectionAddressesProps>()

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const getAiFieldCount = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return aiFilledFields.value.size
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const isAiFieldsStillVisible = () => {
  return getAiFieldCount() > 0
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const clearAIFields = () => {
  // eslint-disable-next-line id-length,@typescript-eslint/no-use-before-define
  for (let i = addressFieldArray.fields.value.length - 1; i >= 0; i--) {
    const basePath = `tw_addresses.${i}`
    const hasAiFields = [
      'address_title',
      'address_line1',
      'address_line2',
      'pincode',
      'city',
      'state',
      'country',
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
    ].some((fieldName) => aiFilledFields.value.has(`${basePath}.${fieldName}`))

    if (hasAiFields) {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      handleRemoveAddress(i, false)
    }
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const acceptAIFields = () => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  aiFilledFields.value.clear()
}

const tfDefault = translationFunctionDefault()

const backendConstantsStore = useBackendConstantsStore()
const { fetchCountryList } = backendConstantsStore
const { countryOptionList } = storeToRefs(backendConstantsStore)

const { setFieldValue } = useFormContext()
const addressFieldArray = useFieldArray<AddressDynamic>('tw_addresses')

const aiFilledFields = ref<Set<string>>(new Set())

const isGermany = (country?: string): boolean => {
  if (!country) return false
  // eslint-disable-next-line id-length
  const c = country.toLowerCase()
  return c === 'germany' || c === 'deutschland'
}

const isInGermanStateOptions = (state: string): boolean => {
  return GERMAN_STATES_OPTIONS.includes({ optionLabel: state, optionValue: state })
}

const handleAddAddress = (): void => {
  addressFieldArray.push({
    address_line1: '',
    address_line2: '',
    address_title: '',
    address_type: 'Other',
    city: '',
    country: 'Germany',
    doctype: 'Address',
    is_primary_address: addressFieldArray.fields.value.length === 0 ? 1 : 0,
    pincode: '',
    state: '',
    tw_alternative_recipient: '',
  })
}

const isPrimaryAddress = (address: AddressDynamic): boolean => {
  if (props.checkPrimary) {
    return props.checkPrimary(address)
  }

  return address.is_primary_address === 1
}

const setPrimaryAddress = async (
  addressItem: AddressDynamic,
  addressItemIndex: number,
): Promise<void> => {
  if (props.setPrimary) {
    if (addressItem.is_primary_address !== undefined) {
      await props.setPrimary({ tw_primary_address: addressItem })

      return
    }
  }

  addressFieldArray.fields.value.forEach((__, currentAddressFieldIndex) => {
    if (currentAddressFieldIndex === addressItemIndex) {
      setFieldValue(`tw_addresses.${currentAddressFieldIndex.toFixed()}.is_primary_address`, 1)
    } else {
      setFieldValue(`tw_addresses.${currentAddressFieldIndex.toFixed()}.is_primary_address`, 0)
    }
  })
  addressFieldArray.move(addressItemIndex, 0)
  setFieldValue('tw_primary_address', addressItem)
}

const handleRemoveAddress = (addressItemIndex: number, submit = true): void => {
  const isPrimary = Boolean(
    addressFieldArray.fields.value[addressItemIndex]?.value.is_primary_address,
  )

  const aiPathsToRemove = [
    'address_title',
    'address_line1',
    'address_line2',
    'pincode',
    'city',
    'state',
    'country',
    'tw_alternative_recipient',
  ]

  aiPathsToRemove.forEach((fieldName) => {
    const fullPath = `tw_addresses.${addressItemIndex}.${fieldName}`
    aiFilledFields.value.delete(fullPath)
  })

  // eslint-disable-next-line id-length
  for (let i = addressItemIndex + 1; i < addressFieldArray.fields.value.length; i++) {
    aiPathsToRemove.forEach((fieldName) => {
      const oldPath = `tw_addresses.${i}.${fieldName}`
      const newPath = `tw_addresses.${i - 1}.${fieldName}`

      if (aiFilledFields.value.has(oldPath)) {
        aiFilledFields.value.delete(oldPath)
        aiFilledFields.value.add(newPath)
      }
    })
  }

  addressFieldArray.remove(addressItemIndex)

  if (
    isPrimary &&
    addressFieldArray.fields.value.length > 0 &&
    addressFieldArray.fields.value[0]?.value
  ) {
    setFieldValue('tw_addresses.0.is_primary_address', 1)
  }

  if (submit) props.onSubmit?.()
}

const addressExistsAndFilled = (index: number): boolean => {
  const address = addressFieldArray.fields.value[index]?.value
  if (!address) return false

  return !!(
    address.address_title ??
    address.address_line1 ??
    address.address_line2 ??
    address.pincode ??
    address.city ??
    address.state
  )
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const fillFields = () => {
  const ai = props.aiData
  if (!ai?.addresses) return

  ai.addresses.forEach((addressObj) => {
    const existingAddresses = addressFieldArray.fields.value.map((address) => address.value)
    if (hasMatchingAiAddress(existingAddresses, addressObj)) return

    const targetIndex = addressFieldArray.fields.value.length

    if (addressFieldArray.fields.value.length <= targetIndex) {
      addressFieldArray.push({
        address_line1: '',
        address_line2: '',
        address_title: '',
        address_type: 'Other',
        city: '',
        country: '',
        doctype: 'Address',
        is_primary_address: 0,
        pincode: '',
        state: '',
        tw_alternative_recipient: '',
      })
    }

    if (!addressExistsAndFilled(targetIndex)) {
      const countryValue =
        findOptionValueFromAiText(addressObj.country, countryOptionList.value) ?? ''
      const otherFields = [
        {
          path: `tw_addresses.${targetIndex}.address_title`,
          value: resolveAiAddressTitle(addressObj.address_title),
        },
        {
          path: `tw_addresses.${targetIndex}.address_line1`,
          value: addressObj.address_line1 || '',
        },
        {
          path: `tw_addresses.${targetIndex}.address_line2`,
          value: addressObj.address_line2 ?? '',
        },
        { path: `tw_addresses.${targetIndex}.pincode`, value: addressObj.pincode || '' },
        { path: `tw_addresses.${targetIndex}.city`, value: addressObj.city || '' },
        { path: `tw_addresses.${targetIndex}.state`, value: addressObj.state ?? '' },
        { path: `tw_addresses.${targetIndex}.country`, value: countryValue },
      ]

      otherFields.forEach(({ path, value }) => {
        const { value: fieldValue } = useField(path)
        if (!fieldValue.value && value) {
          setFieldValue(path, value)
          aiFilledFields.value.add(path)
        }
      })
    }
  })
}

onMounted(async () => {
  await fetchCountryList()
  fillFields()
})

watch(
  () => props.aiData,
  (newVal) => {
    if (newVal) {
      clearAIFields()
      fillFields()
    }
  },
)

defineExpose({
  acceptAIFields,
  clearAIFields,
  getAiFieldCount,
  isAiFieldsStillVisible,
})
</script>

<template>
  <CollapsableContent :title="tfDefault('editRelationGroup.addressesSectionTitle')">
    <ThreedotActionsOnHover
      v-for="(currentAddress, currentAddressIndex) in addressFieldArray.fields.value"
      :key="currentAddress.key"
      class="right-4 top-4"
      menu-align="right"
      visibility="hover"
    >
      <InputGrid :columns="2" :data-testid="TEST_IDS_CREATE.sectionAddresses.inputGrid">
        <TextInput
          class="col-span-2"
          :data-testid="TEST_IDS_CREATE.sectionAddresses.titleInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.address_title`)"
          :label="
            isPrimaryAddress(currentAddress.value)
              ? tfDefault('editRelationGroup.addressTitlePrimary')
              : tfDefault('editRelationGroup.addressTitle')
          "
          :name="`tw_addresses.${currentAddressIndex}.address_title`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="
            () => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.address_title`)
          "
        />
        <TextInput
          v-if="hasAlternativeRecipient"
          class="col-span-2"
          :data-testid="TEST_IDS_CREATE.sectionAddresses.alternativeRecipientInput"
          :in-create-context="inCreateContext ?? false"
          :label="
            props.isPersonCustomer
              ? tfDefault('editRelationGroup.addressAlternativeRecipientPerson')
              : tfDefault('editRelationGroup.addressAlternativeRecipient')
          "
          :name="`tw_addresses.${currentAddressIndex}.tw_alternative_recipient`"
          on-white-bg
          @blur="onSubmit"
        />
        <TextInput
          class="col-span-2"
          :data-testid="TEST_IDS_CREATE.sectionAddresses.addressLine1Input"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.address_line1`)"
          :label="tfDefault('editRelationGroup.addressStreetAndNumber')"
          :name="`tw_addresses.${currentAddressIndex}.address_line1`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="
            () => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.address_line1`)
          "
        />
        <TextInput
          class="col-span-2"
          :data-testid="TEST_IDS_CREATE.sectionAddresses.addressLine2Input"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.address_line2`)"
          :label="tfDefault('editRelationGroup.addressMailBox')"
          :name="`tw_addresses.${currentAddressIndex}.address_line2`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="
            () => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.address_line2`)
          "
        />
        <TextInput
          :data-testid="TEST_IDS_CREATE.sectionAddresses.pincodeInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.pincode`)"
          :label="tfDefault('editRelationGroup.addressZipCode')"
          :name="`tw_addresses.${currentAddressIndex}.pincode`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.pincode`)"
        />
        <TextInput
          :data-testid="TEST_IDS_CREATE.sectionAddresses.cityInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.city`)"
          :label="tfDefault('editRelationGroup.addressCity')"
          :name="`tw_addresses.${currentAddressIndex}.city`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.city`)"
        />
        <FormSelect
          v-if="
            isGermany(currentAddress.value.country) &&
            ((currentAddress.value.state && isInGermanStateOptions(currentAddress.value.state)) ||
              !currentAddress.value.state)
          "
          :data-testid="TEST_IDS_CREATE.sectionAddresses.stateInput"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.state`)"
          :label="tfDefault('editRelationGroup.addressFederalState')"
          :name="`tw_addresses.${currentAddressIndex}.state`"
          on-white-bg
          :options="[GERMAN_STATES_OPTIONS]"
          searchable
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.state`)"
        />
        <TextInput
          v-else
          key="text-input-2"
          :data-testid="TEST_IDS_CREATE.sectionAddresses.stateInput"
          :in-create-context="inCreateContext ?? false"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.state`)"
          :label="tfDefault('editRelationGroup.addressFederalState')"
          :name="`tw_addresses.${currentAddressIndex}.state`"
          on-white-bg
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.state`)"
        />
        <FormSelect
          :data-testid="TEST_IDS_CREATE.sectionAddresses.countrySelect"
          :is-filled-by-ai="aiFilledFields.has(`tw_addresses.${currentAddressIndex}.country`)"
          :label="tfDefault('editRelationGroup.addressCountry')"
          :name="`tw_addresses.${currentAddressIndex}.country`"
          on-white-bg
          :options="[countryOptionList]"
          searchable
          @blur="onSubmit"
          @manual-input="() => aiFilledFields.delete(`tw_addresses.${currentAddressIndex}.country`)"
        />
      </InputGrid>

      <template #actions>
        <div v-if="!isPrimaryAddress(currentAddress.value)">
          <ContextMenuItem
            :data-testid="TEST_IDS_CREATE.sectionAddresses.setPrimaryButton"
            :icon="CheckBadgeIcon"
            :text="tfDefault('editRelationGroup.addressSetPrimaryButton')"
            @click="() => setPrimaryAddress(currentAddress.value, currentAddressIndex)"
          />
          <DivideContent />
        </div>
        <ContextMenuItem
          :data-testid="TEST_IDS_CREATE.sectionAddresses.removeButton"
          :icon="TrashIcon"
          :text="tfDefault('editRelationGroup.remove')"
          @click="() => handleRemoveAddress(currentAddressIndex)"
        />
      </template>
    </ThreedotActionsOnHover>
    <ButtonTertiary
      :data-testid="TEST_IDS_CREATE.sectionAddresses.addButton"
      :disabled="addAddressesDisabled"
      :label="tfDefault('editRelationGroup.addAddressButtonLabel')"
      @click="handleAddAddress"
    >
      <PlusCircleIcon class="mr-2 h-6 w-6" />
    </ButtonTertiary>
  </CollapsableContent>
</template>
